import Spin from '@/components/basic/spin';
import theme from '@/style';
import {ScrollView, StyleSheet, View} from 'react-native';
import ResultTab from '../components/result-tab/result-tab';
import React from 'react';
import {
  DigitDrawResultItem,
  PickGamesContent,
  getQuickPickData,
  getQuickPickResult,
} from '@/common-pages/pick-digit/digit-service';
import ResultHeader from '@/common-pages/pick-digit/digit-tab/digit-result/result-header';
import ResultItem from '@/common-pages/pick-digit/digit-tab/digit-result/result-item';
import DigitDraw from './draw';
import NoData from '@/components/basic/error-pages/no-data';
import useResultScrollLoadMore from '../hooks/use-result-scroll-load-more';

const pageSize = 10;

const QuickDigitResult = ({isActive}: {isActive?: boolean}) => {
  const [loading, setLoading] = React.useState(false);
  const [configId, setConfigId] = React.useState<string>('');
  const [times, setTimes] = React.useState<PickGamesContent[]>([]);
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [list, setList] = React.useState<DigitDrawResultItem[]>([]);
  const loadedRef = React.useRef(false);
  const loadMoreState = useResultScrollLoadMore<DigitDrawResultItem>();

  const getTimes = async () => {
    try {
      setLoading(true);
      const res = await getQuickPickData();
      if (res) {
        setTimes(res.pickGames || []);
        if (res.pickGames.length > 0) {
          setConfigId(`${res.pickGames[0].pickThreeID}`);
        }
      }
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  };

  const getQuickResult = async (
    id: number,
    pageNumber: number = 1,
    loadMore = false,
  ) => {
    if (!firstLoad) {
      setLoading(true);
    }
    try {
      if (id) {
        const res = await getQuickPickResult({
          pageNo: pageNumber,
          pickThreeId: id,
          pageSize,
        });
        if (res) {
          const currentList = res.drawResultList || [];
          const nextList = loadMoreState.syncList(
            currentList,
            loadMore,
            setList,
          );
          setTotal(res.totalCount);
          loadMoreState.hasMoreRef.current = nextList.length < res.totalCount;
        }
      }
    } finally {
      setFirstLoad(false);
      setLoading(false);
      loadMoreState.stopLoadingMore();
    }
  };

  React.useEffect(() => {
    if (isActive && !loadedRef.current) {
      loadedRef.current = true;
      getTimes();
    }
  }, [isActive]);

  React.useEffect(() => {
    if (configId && isActive) {
      loadMoreState.reset(setList);
      getQuickResult(+configId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId, isActive]);

  return (
    <Spin loading={loading} style={[theme.flex.flex1]}>
      <ScrollView
        stickyHeaderIndices={[1]}
        scrollEventThrottle={16}
        onScroll={e => {
          loadMoreState.handleScroll(e, {
            disabled: !configId || loading,
            onLoadMore: nextPage => {
              getQuickResult(+configId, nextPage, true);
            },
          });
        }}>
        {!firstLoad && configId && isActive ? (
          <DigitDraw id={+configId} />
        ) : (
          <View />
        )}
        <ResultTab
          selectKey={configId}
          onSelectKey={setConfigId}
          tabs={times.map(item => {
            return {
              label: `${item.cycle}`,
              key: `${item.pickThreeID}`,
            };
          })}
        />
        {total > 0 ? (
          <View style={styles.listWrap}>
            <ResultHeader isQuick />
            {list.map((item, _i) => (
              <ResultItem
                key={_i}
                issueNum={item.pickNo}
                index={_i}
                result={item.drawResult}
                time={item.drawTime}
                isQuick
              />
            ))}
          </View>
        ) : !firstLoad && !loading ? (
          <NoData />
        ) : null}
      </ScrollView>
    </Spin>
  );
};

const styles = StyleSheet.create({
  listWrap: {
    flexDirection: 'column',
    overflow: 'hidden',
    paddingBottom: theme.paddingSize.l,
  },
});

export default QuickDigitResult;
